import { useCallback, useState } from "react";
import { createViewChartOfAccounts } from "@/ui/views";
import type { TChartOfAccountsQuery, TChartOfAccountsRow } from "@/ui/views";
import { NOOP } from "@/utils/utils";
import type { TCompositionDeps } from "./types";
import type { TAccountDraft } from "@/ui/views/ViewChartOfAccounts/ChartOfAccountsFormModal";

type TPageState = {
    items: TChartOfAccountsRow[];
    total: number;
    totalUnfiltered: number;
    isLoading: boolean;
    hasError: boolean;
};

const EMPTY: TPageState = {
    items: [],
    total: 0,
    totalUnfiltered: 0,
    isLoading: true,
    hasError: false,
};

export const wireSettingsChartOfAccounts = ({
    store: _store,
    useAppStore,
    api,
}: TCompositionDeps) => {
    const ViewChartOfAccounts = createViewChartOfAccounts({
        useCases: () => {
            const userPermissions = useAppStore((state) => state.user?.permissions ?? []);
            const [pageState, setPageState] = useState<TPageState>(EMPTY);

            const onQueryChange = useCallback(
                async (query: TChartOfAccountsQuery, signal?: AbortSignal) => {
                    setPageState((prev) => ({ ...prev, isLoading: true, hasError: false }));
                    try {
                        const data = await api.chartOfAccounts.list(
                            {
                                ...query,
                                // Default view shows active accounts only (so a soft-delete
                                // removes the row); the Active column filter opts into
                                // inactive rows by sending is_active explicitly.
                                active_only: query.is_active === undefined,
                            },
                            signal,
                        );
                        setPageState({
                            items: data.items.map((item) => ({
                                id: item.id,
                                number: item.number,
                                shortName: item.short_name,
                                category: item.category ?? null,
                                isProhibited: item.is_prohibited,
                                isActive: item.is_active,
                            })),
                            total: data.total,
                            totalUnfiltered: data.total_unfiltered ?? data.total,
                            isLoading: false,
                            hasError: false,
                        });
                    } catch (err) {
                        // Swallow abort errors — the superseding request owns the loading state.
                        if (err instanceof DOMException && err.name === "AbortError") return;
                        setPageState((prev) => ({ ...prev, isLoading: false, hasError: true }));
                    }
                },
                [api],
            );

            return {
                onMount: NOOP,
                onUnmount: NOOP,
                onQueryChange,
                useChartOfAccountsPage: () => pageState,
                onCreateAccount: useCallback(
                    async (data: TAccountDraft) => {
                        await api.chartOfAccounts.create(data);
                    },
                    [api],
                ),
                onUpdateAccount: useCallback(
                    async (id: string, data: TAccountDraft) => {
                        await api.chartOfAccounts.update(id, data);
                    },
                    [api],
                ),
                onDeleteAccount: useCallback(
                    async (id: string) => {
                        await api.chartOfAccounts.delete(id);
                    },
                    [api],
                ),
                onUploadFile: useCallback(
                    async (file: File) => api.chartOfAccounts.upload(file),
                    [api],
                ),
                permissions: () => userPermissions,
            };
        },
    });

    return { ViewChartOfAccounts };
};
